/**
 * localStorage cache for lookup reference data.
 *
 * Every lookup endpoint (api/public/lookup/*) is parameterless and returns the same
 * payload for every user, clinic and country, so a single shared cache is safe.
 * The data changes rarely, which lets us render populated dropdowns immediately on
 * load and only go to the network once a cached copy has gone stale.
 *
 * Cache misses are never fatal - if localStorage is unavailable (private browsing,
 * quota exceeded, storage disabled) every operation degrades to "not cached" and the
 * lookup is fetched exactly as it was before.
 */

const CACHE_VERSION = "v1";
const CACHE_PREFIX = `medai.lookup.${CACHE_VERSION}.`;
const LEGACY_PREFIX = "medai.lookup.";
const TTL_MS = 12 * 60 * 60 * 1000; // lookups rarely change; refetch at most once a day

const storageKey = (name) => CACHE_PREFIX + name;

/**
 * Parsed entries, keyed by lookup name.
 *
 * Reading straight from localStorage would hand out freshly parsed objects on every
 * call, so dispatching the same lookup twice (Header and LoginData both request a few
 * of them) would put two different object identities into the store and invalidate any
 * selection already matched by LookupService.matchLookupObject. Memoising keeps one
 * instance per lookup for the lifetime of the page, and skips re-parsing.
 */
const parsed = new Map();

/**
 * Reads a cached lookup.
 * @returns {{data: Array, fresh: boolean}|null} null when absent or unreadable
 */
const read = (name) => {
    if (!parsed.has(name)) {
        parsed.set(name, parseStoredEntry(name));
    }

    const entry = parsed.get(name);

    return entry ? {data: entry.data, fresh: Date.now() - entry.timestamp < TTL_MS} : null;
};

const parseStoredEntry = (name) => {
    try {
        const raw = localStorage.getItem(storageKey(name));

        if (!raw) {
            return null;
        }

        const entry = JSON.parse(raw);

        if (!entry || !Array.isArray(entry.data) || typeof entry.timestamp !== "number") {
            return null;
        }

        return entry;
    } catch (e) {
        return null;
    }
};

/**
 * Stores a freshly fetched lookup.
 *
 * @returns {boolean} true when the value differs from the cached copy and the store
 *          should be updated. Returning false keeps the existing objects in Redux,
 *          which matters because LookupService.matchLookupObject resolves values by
 *          object identity - replacing an unchanged array would blank out any select
 *          that has already matched against it.
 */
const store = (name, data) => {
    if (!Array.isArray(data)) {
        return true; // nothing worth caching, but let the caller dispatch it
    }

    const cached = read(name);
    const changed = !cached || JSON.stringify(cached.data) !== JSON.stringify(data);
    const timestamp = Date.now();

    // On an unchanged payload the caller keeps whatever is already in the store, so the
    // memoised instance must be kept too - swapping in the newly fetched array here
    // would hand a different identity to the next reader of the same lookup.
    parsed.set(name, {timestamp, data: changed ? data : cached.data});

    try {
        localStorage.setItem(storageKey(name), JSON.stringify({timestamp, data}));
    } catch (e) {
        // best effort only - a full or unavailable storage just means no caching
    }

    return changed;
};

/**
 * Drops entries written by earlier cache versions so a version bump cannot leave
 * orphaned payloads behind in localStorage.
 */
const purgeOutdatedVersions = () => {
    try {
        Object.keys(localStorage)
            .filter(key => key.startsWith(LEGACY_PREFIX) && !key.startsWith(CACHE_PREFIX))
            .forEach(key => localStorage.removeItem(key));
    } catch (e) {
        // ignore - purging is housekeeping, not a requirement
    }
};

purgeOutdatedVersions();

export default {read, store};
